import { getModuleUrl } from "@dehub/theme";
import { HttpRequest } from "../utils";

const httpRequest = new HttpRequest();

export interface IDateRange {
  [key: string]: string;
  date: string;
  startDate: string;
  endDate: string;
}

// Order dashboard — requires date, startDate, endDate, pieFilter
export const getOrderDashboard = async (params: IDateRange) => {
  return await httpRequest.get("/ord/dashboard/main", {
    ...params,
    pieFilter: "MONTH",
  }, { silent: true });
};

const buildOrderFilter = (type: string, startDate = "", endDate = ""): Record<string, any> => {
  const base = { type, orderStatus: "", businessId: "", paymentStatus: "", paymentTermCondition: "" };
  if (type) {
    return startDate || endDate ? { ...base, createdAt: [startDate || null, endDate || null] } : base;
  }
  return { ...base, startDate, endDate, invoiceStatus: "", excel: false };
};

export const getMyOrders = async (limit = 5, type = ""): Promise<{ rows: any[]; count: number }> => {
  return await httpRequest.post("/ord/order/list", {
    filter: buildOrderFilter(type),
    offset: { limit, page: 1 },
  }, { silent: true });
};

export const getOrdersInRange = async (startDate: string, endDate: string, limit = 1000, type = ""): Promise<{ rows: any[]; count: number }> => {
  return await httpRequest.post("/ord/order/list", {
    filter: buildOrderFilter(type, startDate, endDate),
    offset: { limit, page: 1 },
  }, { silent: true });
};

// Invoice dashboard — different param names
export const getInvoiceDashboard = async (params: IDateRange) => {
  return await httpRequest.get("/inv/dashboard/main", {
    date: params.date,
    confirmedStartDate: params.startDate,
    confirmedEndDate: params.endDate,
    pendingStartDate: params.startDate,
    pendingEndDate: params.endDate,
  }, { silent: true });
};

// Payment dashboard — requires date, startDate, endDate, structureDate
export const getPaymentDashboard = async (params: IDateRange) => {
  return await httpRequest.get("/pay/dashboard/main", {
    ...params,
    structureDate: params.date,
  }, { silent: true });
};

// Inventory dashboard — requires date, rowDate, startDate, endDate
export const getInventoryDashboard = async (params: IDateRange) => {
  return await httpRequest.get("/erp/dashboard/main", {
    ...params,
    rowDate: params.date,
  }, { silent: true });
};

// Network/partner dashboard — only requires date
export const getNetworkDashboard = async (params: IDateRange) => {
  return await httpRequest.get("/biz/dashboard/main", {
    date: params.date,
  }, { silent: true });
};

// Quotation stats — no date params
export const getQuotationStats = async () => {
  return await httpRequest.get("/pricing/quotation/stats", {}, { silent: true });
};

export const getReceivedQuotationCount = async (status?: string): Promise<{ count: number }> => {
  try {
    return await httpRequest.get(
      `${getModuleUrl("price")}/pricing/received-quotation`,
      {
        offset: { page: 1, limit: 1 },
        ...(status ? { filter: { status } } : {}),
      },
      { silent: true },
    );
  } catch {
    return { count: 0 };
  }
};

// Pricing overview — no date params
export const getPricingOverview = async () => {
  return await httpRequest.get("/pricing/dashboard/overview", {}, { silent: true });
};

// Contract dashboard
export const getContractDashboard = async (params: IDateRange) => {
  return await httpRequest.post("/ctr/dashboard", { date: params.date }, { silent: true });
};

// Promotion performance — new endpoint
export const getPromotionPerformance = async (date?: string) => {
  return await httpRequest.get("/pricing/promotion/performance", date ? { date } : {}, { silent: true });
};

export const getPullSheetCount = async (pullSheetStatus: string): Promise<number> => {
  const res = (await httpRequest.post(
    "/ord/pull_sheet/list",
    {
      filter: {
        refCode: "",
        loadingDate: [null, null],
        pullSheetStatus,
        deliveryDate: [null, null],
        staffFirstNameEmail: "",
        deliveryNoteCount: null,
        warehouseRefCodeName: "",
      },
      offset: { page: 1, limit: 1 },
      order: ["createdAt", "DESC"],
    } as any,
    { silent: true },
  )) as { count?: number } | null;
  return res?.count ?? 0;
};

export const getDeliveryNoteCount = async (deliveryNoteStatus: string): Promise<number> => {
  const res = (await httpRequest.post(
    "/ord/delivery_note/list",
    {
      filter: { deliveryNoteStatus },
      offset: { page: 1, limit: 1 },
    } as any,
    { silent: true },
  )) as { count?: number } | null;
  return res?.count ?? 0;
};

export const getConsignmentReceiveCount = async (businessId: string): Promise<number> => {
  if (!businessId) return 0;
  const res = (await httpRequest.post(
    "/cng/deliveries/list",
    {
      filter: { businessId, role: "RETAILER", status: "DELIVERED" },
      offset: { page: 1, limit: 1 },
    } as any,
    { silent: true },
  )) as { count?: number } | null;
  return res?.count ?? 0;
};

export const getStockInouts = async (limit = 8): Promise<any[]> => {
  const res = (await httpRequest.post(
    "/erp/stock/inouts/list",
    {
      filter: {
        itemFilter: { isActive: true },
        inOutFilter: {},
        columnFilter: { createdAt: [null, null] },
      },
      offset: { page: 1, limit },
      order: ["createdAt", "DESC"],
    } as any,
    { silent: true },
  )) as { rows?: any[] } | null;
  return res?.rows ?? [];
};
